import React from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { useCreateDocumentDialog, useGenericDocumentPermissions } from "components/documents";
import { useCreateInvoice, useInvoicePermissions } from "components/invoice";
import { useCreateWeeklyTimeReport } from "components/timeReporting";
import { useCreateAllowanceDialog, useCreateLeaveDialog } from "components/salaryDeviation";
import { ActionButton, useCheckRelations } from "components/common";
import { useCashReconciliationPermissions, useCreateCashReconciliationDialog } from "components/cashReconciliation";
import { useCreateRevenueReport } from "components/revenueReporting";
import { Grid } from "@mui/material";
import { useCompanyService } from "serviceHooks";
import { useIncomingInvoicePermissions } from "components/incomingInvoice";
import { useReceiptPermissions } from "components/receipt";
import { useSalaryPermissions } from "components/salary";
//#endregion
import cssExports from "theme/styles/main.module.scss";

const DashboardActions: React.FC = () => {
  const companyService = useCompanyService();

  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  //permissions
  const { getIsAuditor } = useCheckRelations();
  const isAuditor = getIsAuditor();
  const { canCreateIncomingInvoices } = useIncomingInvoicePermissions();
  const { canUseInvoices } = useInvoicePermissions();

  const { isSalaryDeviationsAvailable } = useSalaryPermissions();
  const isTimeReportingEnabled = companyService.canLoggedInUserReportTime();
  const { receiptCreator } = useReceiptPermissions();
  const { canCreateGenericDocuments } = useGenericDocumentPermissions();
  const isDocumentCreationApplicable = receiptCreator || canCreateIncomingInvoices || canCreateGenericDocuments;
  const { canCreateCashReconciliations } = useCashReconciliationPermissions();

  //dialogs
  const createDocumentDialog = useCreateDocumentDialog();
  const onCreateInvoice = useCreateInvoice();
  const onCreateWeeklyTimeReport = useCreateWeeklyTimeReport();
  const createLeaveDialog = useCreateLeaveDialog();
  const createAllowanceDialog = useCreateAllowanceDialog();
  const cashReconciliationDialog = useCreateCashReconciliationDialog();
  const onCreateRevenueReport = useCreateRevenueReport(cashReconciliationDialog);

  async function onCreateDocumentClick() {
    return dispatch(displayDialog(createDocumentDialog));
  }

  async function onCreateLeaveDialogClick() {
    return dispatch(displayDialog(createLeaveDialog()));
  }

  async function onCreateAllowanceClick() {
    return dispatch(displayDialog(createAllowanceDialog()));
  }

  async function onCreateRevenueReportClick() {
    return onCreateRevenueReport();
  }

  const initialbuttons = [
    {
      icon: IconDefinitionConstants.WDocument,
      btnText: TranslationKeys.Common.AddDocument,
      onClickHandler: onCreateDocumentClick,
      hidden: isAuditor || !isDocumentCreationApplicable,
    },
    {
      icon: IconDefinitionConstants.WPaperPlane,
      btnText: TranslationKeys.Common.CreateCustomerInvoice,
      onClickHandler: onCreateInvoice,
      hidden: isAuditor || !canUseInvoices,
    },
    {
      icon: IconDefinitionConstants.WTimer,
      btnText: TranslationKeys.Common.RegisterTime,
      onClickHandler: onCreateWeeklyTimeReport,
      hidden: isAuditor || !isTimeReportingEnabled,
    },
    {
      icon: IconDefinitionConstants.WCalendar,
      btnText: TranslationKeys.SalaryDeviation.AddTimeDeviation,
      onClickHandler: onCreateLeaveDialogClick,
      hidden: isAuditor || !isSalaryDeviationsAvailable,
    },
    {
      icon: IconDefinitionConstants.WMoney,
      btnText: TranslationKeys.SalaryDeviation.CreateAllowance,
      onClickHandler: onCreateAllowanceClick,
      hidden: isAuditor || !isSalaryDeviationsAvailable,
    },
    {
      icon: IconDefinitionConstants.WCalculator,
      btnText: TranslationKeys.Common.AddDailyTakings,
      onClickHandler: onCreateRevenueReportClick,
      hidden: isAuditor || !canCreateCashReconciliations(),
    },
  ];

  const mappedbuttons = initialbuttons
    .filter((x) => !x.hidden)
    .slice(0, 4)
    .map((button, index) => (
      <Grid key={index} item xs className={cssExports.dashboardActionButtonContainer}>
        <ActionButton icon={button.icon} btnText={button.btnText} onClickHandler={button.onClickHandler} />
      </Grid>
    ));

  return (
    <Grid item container className={cssExports.dashboardActionsContainer} display="row" spacing={2}>
      {mappedbuttons}
    </Grid>
  );
};

export default DashboardActions;
